import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import api from "../services";
import { useAuth } from "../context/AuthContext";
import { useOrder } from "../context/OrderContext";
import { formatScheduledDateForDisplay } from "../lib/utils";
type orderDeliveryStatus =
  | "pending"
  | "pickup"
  | "pickup_complete"
  | "dropoff"
  | "received"
  | "delivered"
  | "canceled";

interface OrderTrackingToastProps {
  status: orderDeliveryStatus;
  orderId?: string;
  maplink?: string;
  onHide?: () => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  isDragging?: boolean;
  currentIndex: number;
  totalOrders: number;
  orderStatus?: string;
  scheduledAt?: Date | string;
  scheduledTimezone?: string; // IANA timezone identifier
  onNextOrder?: () => void;
  onPrevOrder?: () => void;
}

const OrderTrackingToast: React.FC<OrderTrackingToastProps> = ({
  status,
  orderId,
  maplink,
  onHide,
  onMouseDown,
  onTouchStart,
  isDragging,
  currentIndex,
  totalOrders,
  orderStatus,
  scheduledAt,
  scheduledTimezone,
  onNextOrder,
  onPrevOrder,
}) => {
  // Use the orderDeliveryStatus type for the keys and define the display for orderDeliveryStatus values
  const stages: Record<orderDeliveryStatus, { text: string; color: string }> = {
    pending: { text: "⌛ Waiting for courier...", color: "bg-black" },
    received: { text: "⌛ Waiting for courier...", color: "bg-black" },
    pickup: { text: "🛵 Pickup in progress", color: "bg-black" },
    pickup_complete: { text: "📦 Order picked up", color: "bg-black" },
    dropoff: { text: "🚚 Out for delivery", color: "bg-black" },
    delivered: { text: "🎉 Order delivered!", color: "bg-black" },
    canceled: { text: "Order Canceled", color: "bg-black" },
  };

  // Map progress for orderDeliveryStatus values
  const progressMap: Record<orderDeliveryStatus, number> = {
    pending: 20,
    pickup: 35,
    pickup_complete: 60,
    dropoff: 85,
    received: 20,
    delivered: 100,
    canceled: 0,
  };

  const handleTrackClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (maplink) {
      window.open(maplink, "_blank");
    }
  };

  const handleHideClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onHide?.();
  };

  const handlePrevClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPrevOrder?.();
  };

  const handleNextClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onNextOrder?.();
  };

  const { text, color } = stages[status];
  const progress = progressMap[status];
  return (
    <div
      className="order-toast flex items-center justify-between w-full p-3 bg-[#ffffff] rounded-xl
       shadow-lg border border-orange-300 select-none"
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      style={{ cursor: isDragging ? "grabbing" : "grab" }}
    >
      {/* Left side: Order details */}
      <div className="flex-1 min-w-0 font-sans pointer-events-none pr-2">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-semibold text-[#1f2937]">
            🍔 Order {orderId ? `#${orderId}` : "Update"}
          </h4>
          {/* Order counter */}
          {totalOrders > 1 && (
            <span className="text-xs text-[#000000] bg-toggle px-2 py-0.5 rounded-full ml-1 shrink-0">
              {currentIndex + 1} of {totalOrders}
            </span>
          )}
        </div>
        <p className="text-xs text-[#4b5563] mb-2">
 {orderStatus === "scheduled" && scheduledAt && (status === "pending" || status === "received")
  ? `Scheduled for ${
      scheduledTimezone
        ? formatScheduledDateForDisplay(
            typeof scheduledAt === 'string' ? scheduledAt : scheduledAt.toISOString(),
            scheduledTimezone
          ).full
        : new Date(scheduledAt).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit"
          })
    }`
  : text}
        </p>

        {/* Progress bar */}
        <div className="w-full h-1.5 bg-[#e5e7eb] rounded-full overflow-hidden">
          <div
            className={`${color} h-1.5 transition-all duration-500 ease-in-out`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Stage labels */}
        <div className="flex justify-between mt-1.5 text-[10px] text-[#6b7280]">
          <span>Received</span>
          <span>Preparing</span>
          <span>Ready</span>
          <span>Delivered</span>
        </div>
      </div>

      {/* Right side: Navigation and Action buttons */}
      <div className="flex flex-col justify-between pointer-events-auto shrink-0 gap-1">
        {/* Navigation arrows - only show if multiple orders */}
        {totalOrders > 1 ? (
          <div className="flex gap-2">
            <button
              onClick={handlePrevClick}
              disabled={currentIndex === 0}
              className="dm-recent-order-nav bg-[#d1d5db] disabled:bg-[#e5e7eb] disabled:cursor-not-allowed text-[#000000] px-2 py-1 text-xs rounded hover:bg-[#9ca3af] transition-colors"
              title="Previous order"
            >
              ←
            </button>
            <button
              onClick={handleNextClick}
              disabled={currentIndex === totalOrders - 1}
              className="dm-recent-order-nav bg-[#d1d5db] disabled:bg-[#e5e7eb] disabled:cursor-not-allowed text-[#000000] px-2 py-1 text-xs rounded hover:bg-[#9ca3af] transition-colors"
              title="Next order"
            >
              →
            </button>
          </div>
        ) : (
          <div className="h-6" />
        )}

        {/* Action buttons */}
        {maplink ? (
          <button
            onClick={handleTrackClick}
            className="dm-recent-order-btn bg-[#ffffff] text-[#000000] border border-[#00000033] px-3 py-1 text-xs rounded transition-colors hover:bg-[#f3f4f6]"
          >
            View
          </button>
        ) : (
          <div className="h-6" />
        )}
        <button
          onClick={handleHideClick}
          className="dm-recent-order-hide bg-[#d1d5db] text-[#000000] px-3 py-1 text-xs rounded transition-colors hover:bg-[#9ca3af]"
        >
          Hide
        </button>
      </div>
    </div>
  );
};

interface OrderTrackerProps {
  phoneNumber: string;
  pollingInterval?: number;
  onStatusChange?: (status: orderDeliveryStatus) => void;
  /** When false, the minimized "Recent Order Detail" button is not rendered inline.
   *  Use this when you render the trigger button externally (e.g. in a header). */
  renderButton?: boolean;
  /** Called whenever shouldShowTracker, isMinimized, or totalActiveOrders changes. */
  onStateChange?: (
    shouldShow: boolean,
    isMinimized: boolean,
    count: number,
  ) => void;
}

export interface OrderTrackerHandle {
  handleShow: () => void;
}

const OrderTracker = forwardRef<OrderTrackerHandle, OrderTrackerProps>(
  (
    {
      phoneNumber,
      pollingInterval = 5000,
      onStatusChange,
      renderButton = true,
      onStateChange,
    },
    ref,
  ) => {
    const { fetchOrderStatus, recentOrder } = useOrder();
    const { user } = useAuth();
    const lastOrdersRef = useRef<any[]>([]);
    const lastOrderIndexRef = useRef<number>(0);
    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const toastRef = useRef<HTMLDivElement>(null);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [currentOrderIndex, setCurrentOrderIndex] = useState(0);

    // Draggable state — initialise at bottom-center, clamped so it's always on-screen
    const getInitialPosition = () => ({
      x: Math.max(8, window.innerWidth / 2 - 200),
      y: window.innerHeight - 200,
    });

    const [position, setPosition] = useState(getInitialPosition);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    // Get current orders array
    const currentOrders = useMemo(() => {
      return Array.isArray(recentOrder) ? recentOrder : [];
    }, [recentOrder]);

    // Get current order from array
    const currentOrder =
      currentOrders.length > 0 ? currentOrders[currentOrderIndex] : null;
    const totalOrders = currentOrders.length;

    // Function to preserve current order index when possible
    const preserveOrderIndex = useCallback(
      (newOrders: any[], oldOrders: any[]) => {
        if (!oldOrders.length || !newOrders.length) {
          return 0;
        }

        const currentOrderId = oldOrders[lastOrderIndexRef.current]?.orderNumber;
        if (!currentOrderId) return 0;

        // Find the same order in the new array
        const foundIndex = newOrders.findIndex(
          (order) => order.orderNumber === currentOrderId,
        );
        return foundIndex !== -1 ? foundIndex : 0;
      },
      [],
    );

    // ── Mouse drag handlers ──────────────────────────────────────────────────
    const handleMouseDown = (e: React.MouseEvent) => {
      if (
        (e.target as HTMLElement).tagName === "BUTTON" ||
        (e.target as HTMLElement).closest("button")
      ) {
        return;
      }

      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        const toastWidth = Math.min(400, window.innerWidth - 16);
        const maxX = window.innerWidth - toastWidth - 8;
        const maxY = window.innerHeight - 150;

        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    // ── Touch drag handlers (mobile) ─────────────────────────────────────────
    const handleTouchStart = (e: React.TouchEvent) => {
      if (
        (e.target as HTMLElement).tagName === "BUTTON" ||
        (e.target as HTMLElement).closest("button")
      ) {
        return;
      }
      const touch = e.touches[0];
      setIsDragging(true);
      setDragOffset({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y,
      });
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (isDragging) {
        const touch = e.touches[0];
        const newX = touch.clientX - dragOffset.x;
        const newY = touch.clientY - dragOffset.y;
        const toastWidth = Math.min(400, window.innerWidth - 16);
        const maxX = window.innerWidth - toastWidth - 8;
        const maxY = window.innerHeight - 150;

        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        });
        e.preventDefault(); // prevent page scroll while dragging
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    // Handle outside click to compress modal
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          toastRef.current &&
          !toastRef.current.contains(event.target as Node) &&
          !isDragging
        ) {
          handleHide();
        }
      };

      if (isVisible && !isMinimized) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isVisible, isMinimized, isDragging]);

    useEffect(() => {
      if (isDragging) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        document.addEventListener("touchmove", handleTouchMove, {
          passive: false,
        });
        document.addEventListener("touchend", handleTouchEnd);
        document.body.style.userSelect = "none";
      } else {
        document.body.style.userSelect = "";
      }

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
        document.body.style.userSelect = "";
      };
    }, [isDragging, dragOffset]);

    const handleHide = () => {
      setIsMinimized(true);
      setIsVisible(false);
    };

    const handleShow = () => {
      setIsMinimized(false);
      setIsVisible(true);
    };

    // Expose handleShow to parent via ref
    useImperativeHandle(ref, () => ({ handleShow }));

    const handleNextOrder = () => {
      if (currentOrderIndex < totalActiveOrders - 1) {
        const newIndex = currentOrderIndex + 1;
        setCurrentOrderIndex(newIndex);
        lastOrderIndexRef.current = newIndex;
      }
    };

    const handlePrevOrder = () => {
      if (currentOrderIndex > 0) {
        const newIndex = currentOrderIndex - 1;
        setCurrentOrderIndex(newIndex);
        lastOrderIndexRef.current = newIndex;
      }
    };

    useEffect(() => {
      console.log(`Order is `, recentOrder);
    }, [recentOrder]);

    useEffect(() => {
      fetchOrderStatus();
    }, [user]);

    useEffect(() => {
      console.log(`KKKKgggg Ref Order is `, recentOrder);
    }, [recentOrder]);

    // Handle order updates while preserving current view
    useEffect(() => {
      if (currentOrders.length === 0) {
        setIsVisible(false);
        return;
      }

      // Check if orders have actually changed
      const ordersChanged =
        JSON.stringify(currentOrders) !== JSON.stringify(lastOrdersRef.current);

      if (ordersChanged) {
        // Preserve the current order index if possible
        const preservedIndex = preserveOrderIndex(
          currentOrders,
          lastOrdersRef.current,
        );

        // Only update index if the preserved index is different or if we're starting fresh
        if (
          lastOrdersRef.current.length === 0 ||
          preservedIndex !== currentOrderIndex
        ) {
          setCurrentOrderIndex(preservedIndex);
          lastOrderIndexRef.current = preservedIndex;
        }

        // Update the reference
        lastOrdersRef.current = [...currentOrders];
      }
    }, [currentOrders, preserveOrderIndex, currentOrderIndex]);

    // Handle status changes and visibility
    useEffect(() => {
      if (!currentOrder) return;

      const orderChoice = currentOrder.choice?.toLowerCase();
      if (orderChoice !== "online") {
        setIsVisible(false);
        setIsMinimized(false);
        return;
      }

      const newStatus = currentOrder.deliveryStatus?.toLowerCase();

      if (newStatus) {
        onStatusChange?.(newStatus as orderDeliveryStatus);

        if (!isMinimized) {
          setIsVisible(true);
        }

        // Handle delivered/canceled orders - remove from stack instead of closing popup
        if (newStatus === "delivered" || newStatus === "canceled") {
          // Show a brief notification for the delivered/canceled order
          setTimeout(() => {
            // Check if there are other orders after removing this one
            const filteredOrders = currentOrders.filter((order) => {
              const status = order.deliveryStatus?.toLowerCase();
              return status !== "delivered" && status !== "canceled";
            });

            if (filteredOrders.length === 0) {
              // No more active orders, close the popup
              setIsVisible(false);
            } else {
              // Adjust current index if needed
              if (currentOrderIndex >= filteredOrders.length) {
                setCurrentOrderIndex(0);
                lastOrderIndexRef.current = 0;
              }
            }
          }, 3000); // Show delivered notification for 3 seconds
        }
      }
    }, [
      currentOrder,
      isMinimized,
      onStatusChange,
      currentOrders,
      currentOrderIndex,
    ]);

    // Filter out delivered and canceled orders for display
    const activeOrders = useMemo(() => {
      return currentOrders.filter((order) => {
        const status = order.deliveryStatus?.toLowerCase();
        return status !== "delivered" && status !== "canceled";
      });
    }, [currentOrders]);

    // Get current active order
    const currentActiveOrder =
      activeOrders.length > 0 ? activeOrders[currentOrderIndex] : null;
    const totalActiveOrders = activeOrders.length;

    // Adjust currentOrderIndex if it's out of bounds for active orders
    useEffect(() => {
      if (totalActiveOrders > 0 && currentOrderIndex >= totalActiveOrders) {
        const newIndex = Math.max(0, totalActiveOrders - 1);
        setCurrentOrderIndex(newIndex);
        lastOrderIndexRef.current = newIndex;
      }
    }, [totalActiveOrders, currentOrderIndex]);

    const shouldShowTracker =
      currentActiveOrder?.choice?.toLowerCase() === "online" &&
      totalActiveOrders > 0;

    // Notify parent of visibility/state changes
    useEffect(() => {
      onStateChange?.(shouldShowTracker, isMinimized, totalActiveOrders);
    }, [shouldShowTracker, isMinimized, totalActiveOrders, onStateChange]);

    if (!shouldShowTracker || !currentActiveOrder) {
      return null;
    }

    const orderStatus = currentActiveOrder.orderStatus?.toLowerCase();

    const currentStatus =
      currentActiveOrder.deliveryStatus?.toLowerCase() as orderDeliveryStatus;
    const currentOrderId =
      currentActiveOrder.orderNumber?.toString() || phoneNumber;
    const currentMapLink = currentActiveOrder.maplink;

    const toastWidth = Math.min(400, window.innerWidth - 16);

    return (
      <>
        {/* Main draggable toast */}
        {isVisible && !isMinimized && currentStatus && (
          <div
            ref={toastRef}
            style={{
              position: "fixed",
              left: `${position.x}px`,
              top: `${position.y}px`,
              zIndex: 9999,
              width: `${toastWidth}px`,
            }}
          >
            <OrderTrackingToast
              status={currentStatus}
              orderId={currentOrderId}
              maplink={currentMapLink}
              onHide={handleHide}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              isDragging={isDragging}
              currentIndex={currentOrderIndex}
              totalOrders={totalActiveOrders}
              onNextOrder={handleNextOrder}
              onPrevOrder={handlePrevOrder}
              orderStatus={orderStatus}
              scheduledAt={currentActiveOrder.scheduledAt}
              scheduledTimezone={currentActiveOrder.scheduledTimezone}
            />
          </div>
        )}

        {/* Minimized button — only rendered when renderButton=true (sidebar/desktop) */}
        {renderButton && isMinimized && (
          <div
            className="flex flex-col rounded-[30px] items-center cursor-pointer
              transition font-semibold 2xl:text-[15px] lg:text-[13px] sm:text-[12px] text-xs gap-2"
          >
            <button
              onClick={handleShow}
              className="dm-white-btn  w-full flex items-center justify-center gap-1.5 sm:gap-2
                    rounded-lg px-1 sm:px-3 py-1 sm:py-2.5
                    bg-main/90
                    text-white font-semibold
                    text-xs sm:text-sm md:text-base
                    transition-all duration-200
                    shadow-sm hover:shadow-md"
            >
              <span className="relative z-10 text-[8px] sm:text-sm">
                Recent Order Detail
                {totalActiveOrders > 1 ? `s (${totalActiveOrders})` : ""}
              </span>
            </button>
          </div>
        )}
      </>
    );
  },
);

OrderTracker.displayName = "OrderTracker";

export default OrderTracker;
